import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  AuthState,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  LoginCredentials,
  ResetPasswordPayload,
} from "./auth.types";
import type { PermissionAction } from "./models/permission.model";
import {
  changePassword as changePasswordService,
  forgotPassword as forgotPasswordService,
  login as loginService,
  logout as logoutService,
  refreshToken as refreshTokenService,
  resetPassword as resetPasswordService,
  verifySession,
} from "./auth.service";
import {
  hasPermission as hasPermissionKey,
  hasAnyPermission,
  hasAllPermissions,
  hasResourcePermission,
  getUserPermissionKeys,
} from "../rbac/access";

export interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  changePassword: (payload: ChangePasswordPayload) => Promise<void>;
  forgotPassword: (payload: ForgotPasswordPayload) => Promise<void>;
  resetPassword: (payload: ResetPasswordPayload) => Promise<void>;
  verifySession: () => Promise<void>;
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeys: string[]) => boolean;
  hasAllPermissions: (permissionKeys: string[]) => boolean;
  /** Compat legacy resource/action */
  hasResourcePermission: (
    resource: string,
    action?: PermissionAction
  ) => boolean;
  permissionKeys: string[];
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

function sessionToState(
  session: NonNullable<Awaited<ReturnType<typeof verifySession>>>
): AuthState {
  return {
    user: session.user,
    tenant: session.tenant,
    membership: session.membership,
    tokens: session.tokens,
    isAuthenticated: true,
    isLoading: false,
  };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    membership: null,
    tokens: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const applySession = useCallback(
    (session: Awaited<ReturnType<typeof verifySession>>) => {
      if (session) {
        setState(sessionToState(session));
      } else {
        setState({
          user: null,
          tenant: null,
          membership: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    },
    []
  );

  useEffect(() => {
    let mounted = true;
    const verifyTimeout = window.setTimeout(() => {
      if (mounted) {
        setState((prev) =>
          prev.isLoading
            ? {
                user: null,
                tenant: null,
                membership: null,
                tokens: null,
                isAuthenticated: false,
                isLoading: false,
              }
            : prev
        );
      }
    }, 8000);

    verifySession()
      .then((session) => {
        if (mounted) applySession(session);
      })
      .catch(() => {
        if (mounted) {
          applySession(null);
        }
      })
      .finally(() => {
        if (mounted) window.clearTimeout(verifyTimeout);
      });

    return () => {
      mounted = false;
      window.clearTimeout(verifyTimeout);
    };
  }, [applySession]);

  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const session = await loginService(credentials);
        setState(sessionToState(session));
      } catch (error) {
        setState((prev) => ({ ...prev, isLoading: false }));
        throw error;
      }
    },
    []
  );

  const logout = useCallback(async () => {
    await logoutService();
    applySession(null);
  }, [applySession]);

  const refreshToken = useCallback(async () => {
    if (!state.tokens?.refreshToken) return;
    const tokens = await refreshTokenService(state.tokens.refreshToken);
    setState((prev) => ({ ...prev, tokens }));
  }, [state.tokens?.refreshToken]);

  const changePassword = useCallback(async (payload: ChangePasswordPayload) => {
    await changePasswordService(payload);
  }, []);

  const forgotPassword = useCallback(async (payload: ForgotPasswordPayload) => {
    await forgotPasswordService(payload);
  }, []);

  const resetPassword = useCallback(async (payload: ResetPasswordPayload) => {
    await resetPasswordService(payload);
  }, []);

  const runVerifySession = useCallback(async () => {
    const session = await verifySession();
    applySession(session);
  }, [applySession]);

  const hasPermission = useCallback(
    (permissionKey: string) => hasPermissionKey(state.user, permissionKey),
    [state.user]
  );

  const hasAnyPermissionCb = useCallback(
    (permissionKeys: string[]) => hasAnyPermission(state.user, permissionKeys),
    [state.user]
  );

  const hasAllPermissionsCb = useCallback(
    (permissionKeys: string[]) => hasAllPermissions(state.user, permissionKeys),
    [state.user]
  );

  const hasResourcePermissionCb = useCallback(
    (resource: string, action?: PermissionAction) =>
      hasResourcePermission(state.user, resource, action),
    [state.user]
  );

  const permissionKeys = useMemo(
    () => getUserPermissionKeys(state.user),
    [state.user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      refreshToken,
      changePassword,
      forgotPassword,
      resetPassword,
      verifySession: runVerifySession,
      hasPermission,
      hasAnyPermission: hasAnyPermissionCb,
      hasAllPermissions: hasAllPermissionsCb,
      hasResourcePermission: hasResourcePermissionCb,
      permissionKeys,
    }),
    [
      state,
      login,
      logout,
      refreshToken,
      changePassword,
      forgotPassword,
      resetPassword,
      runVerifySession,
      hasPermission,
      hasAnyPermissionCb,
      hasAllPermissionsCb,
      hasResourcePermissionCb,
      permissionKeys,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
